
import { defineComponent, inject } from 'vue'
import NumericInput from '@/components/shared/NumericInput.vue'
import TextInput from '@/components/shared/TextInput.vue'
import LoadingPulse from '@/components/shared/LoadingPulse.vue'
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import { CaliberService } from '@/services/caliber.service'
import Toaster from '@meforma/vue-toaster'
import CartridgeModal from '@/components/modals/CartridgeModal.vue'
import { Cartridge } from '@/@types/responses/cartridge/Cartridge'
import { CaliberDetails } from '@/@types/responses/caliber/CaliberDetails'
import TrashCanSvg from '@/components/svgs/TrashCanSvg.vue'
import { CartridgeService } from '@/services/cartridge.service'
import AuditInformation from '@/components/elements/AuditInformation.vue'
import { CaliberMessage } from '@/@types/messages/CaliberMessage'
import LockingButton from '@/components/shared/LockingButton.vue'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'

export default defineComponent({
  name: 'Caliber',
  components: {
    AuditInformation,
    CartridgeModal,
    LoadingPulse,
    LockingButton,
    NumericInput,
    TextInput,
    TrashCanSvg
  },
  beforeRouteLeave() {
    if (this.showCartridgeModal) {
      this.showCartridgeModal = false
      return false
    }

    return true
  },
  props: {
    id: {
      type: Number,
      required: true
    }
  },
  setup() {
    const caliberService = inject(PROVIDER_CONSTANTS.caliberService) as CaliberService
    const cartridgeService = inject(PROVIDER_CONSTANTS.cartridgeService) as CartridgeService
    const toast = inject(PROVIDER_CONSTANTS.toaster) as Toaster

    return {
      caliberService,
      cartridgeService,
      toast
    }
  },
  data() {
    let caliber: CaliberDetails|undefined
    let unchangedCaliber: CaliberDetails|undefined
    let cartridgeToEdit: Cartridge|undefined

    return {
      caliber,
      unchangedCaliber,
      cartridgeToEdit,
      loading: true,
      saving: false,
      showCartridgeModal: false
    }
  },
  computed: {
    isDirty(): boolean {
      const a = this.caliber
      const b = this.unchangedCaliber

      return (a?.name !== b?.name ||
              a?.boreDiameter !== b?.boreDiameter) && (a?.name.trim() !== '' && a?.boreDiameter !== undefined && a?.boreDiameter >= 0)
    },
    titleText(): string {
      return !this.caliber || this.caliber.id === 0 ? 'Create Caliber' : 'Edit Caliber'
    }
  },
  watch: {
    '$route': {
      handler: function(): void {
        this.updatePageTitle()
      }
    }
  },
  mounted() {
    if (this.id > 0) {
      this.getCaliber()
    } else {
      this.caliber = new CaliberDetails(0)
      this.unchangedCaliber = new CaliberDetails(0)
      this.loading = false
    }
  },
  methods: {
    getCaliber(): Promise<void> {
      return this.caliberService.getCaliber(this.caliber ? this.caliber.id : this.id).then(caliber => {
        this.caliber = caliber
        this.unchangedCaliber = JSON.parse(JSON.stringify(caliber))
        this.updatePageTitle()
        this.loading = false
      }).catch(error => {
        this.saving = false

        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the caliber. Try reloading the page.')
        }
      })
    },
    onAddCartridgeClicked(): void {
      this.cartridgeToEdit = new Cartridge(0)
      this.showCartridgeModal = true
    },
    onCartridgeCreated(): void {
      this.getCaliber().then(() => {
        this.toast.success('Cartridge created successfully.')
      })
    },
    onCartridgeUpdated(): void {
      this.getCaliber().then(() => {
        this.toast.success('Cartridge updated successfully.')
      })
    },
    onDeleteCartridgeClicked(cartridgeId: number): void {
      this.cartridgeService.deleteCartridge(cartridgeId).then(() => {
        this.getCaliber().then(() => {
          this.toast.success('Cartridge deleted successfully.')
        })
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to delete the cartridge. Please try again.')
        }
      })
    },
    onSaveCaliberClicked(): void {
      if (!this.caliber) return

      this.saving = true

      const caliberMessage = new CaliberMessage(this.caliber.name, this.caliber.boreDiameter)
      if (this.caliber.id === 0)
        this.caliberService.createCaliber(caliberMessage).then(objectCreatedResponse => {
          if (!this.caliber) return

          this.caliber.id = objectCreatedResponse.id
          this.getCaliber().then(() => {
            this.$router.replace(`/caliber/${objectCreatedResponse.id}`)
            this.saving = false
            this.toast.success('The caliber was created successfully.')
          })
        }).catch(error => {
          this.saving = false

          if (error instanceof ErrorsResponse) {
            const errorsResponse = error as ErrorsResponse

            if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

            this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
          } else {
            this.toast.error('Failed to create the caliber. Please try again.')
          }
        })
      else
        this.caliberService.updateCaliber(this.id, caliberMessage).then(() => {
          this.toast.success('The caliber was created successfully.')
          this.$router.push('/calibers')
        }).catch(error => {
          this.saving = false

          if (error instanceof ErrorsResponse) {
            const errorsResponse = error as ErrorsResponse

            if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

            this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
          } else {
            this.toast.error('Failed to update the caliber. Please try again.')
          }
        })
    },
    onTableRowClicked(cartridge: Cartridge): void {
      this.cartridgeToEdit = cartridge
      this.showCartridgeModal = true
    },
    updatePageTitle(): void {
      if (this.id > 0 && this.caliber)
        document.title = `${this.caliber.name} - Short Action Customs, LLC`
    }
  }
})
